// src/pages/statistics/GeneralStatisticsPage.tsx
// "Общая статистика" — сводка заказов по периодам (дни/недели/месяцы/года) с разбивкой
// по сегменту (клиенты/распространители/API) и раскрытием периода по мероприятиям и городам.
import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import {
    getGeneralStatisticsRequest,
    getGeneralStatisticsEventsRequest,
    GeneralStatsPeriod,
    GeneralStatsSegment,
    GeneralStatsPeriodRow,
    GeneralStatsEventRow,
} from "../../services/api/statistics.api";
import { formatMoney } from "./statsFormat";
import BuyerTypeStatistics from "./BuyerTypeStatistics";

const PERIOD_OPTIONS: { value: GeneralStatsPeriod; label: string; columnLabel: string }[] = [
    { value: "day", label: "Дни", columnLabel: "Дата" },
    { value: "week", label: "Недели", columnLabel: "Неделя" },
    { value: "month", label: "Месяцы", columnLabel: "Месяц" },
    { value: "year", label: "Годы", columnLabel: "Год" },
];

const SEGMENT_OPTIONS: { value: GeneralStatsSegment; label: string }[] = [
    { value: "clients", label: "Клиенты" },
    { value: "distributors", label: "Распространители" },
    { value: "api", label: "API" },
    // Вечерняя касса: специальный пользователь, в «Клиентах» не учитывается.
    { value: "abendkasse", label: "Abendkasse" },
];

const PAGE_SIZE = 30;

// Кэш раскрытых мероприятий по конкретному периоду (ключ = period_start периода).
interface EventsState {
    status: "loading" | "loaded" | "error";
    events?: GeneralStatsEventRow[];
    error?: string;
}

const GeneralStatisticsPage: React.FC = () => {
    const [period, setPeriod] = useState<GeneralStatsPeriod>("day");
    // Отдельная вкладка «По типу покупателя» (не по периодам).
    const [buyerTypeView, setBuyerTypeView] = useState(false);
    const [segment, setSegment] = useState<GeneralStatsSegment>("clients");

    const [rows, setRows] = useState<GeneralStatsPeriodRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);

    // Раскрытые периоды (по key) и загруженные для них мероприятия.
    const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());
    const [eventsByPeriod, setEventsByPeriod] = useState<Record<string, EventsState>>({});
    // Раскрытые мероприятия внутри периода: `${periodKey}:${tourId}`.
    const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

    const columnLabel = useMemo(
        () => PERIOD_OPTIONS.find((p) => p.value === period)?.columnLabel || "Дата",
        [period]
    );

    const loadPage = async (offset: number, append: boolean) => {
        if (append) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
            setExpandedPeriods(new Set());
            setEventsByPeriod({});
            setExpandedEvents(new Set());
        }
        setPageError(null);
        try {
            const res = await getGeneralStatisticsRequest({
                period,
                segment,
                limit: PAGE_SIZE,
                offset,
            });
            setRows((prev) => (append ? [...prev, ...res.periods] : res.periods));
            setTotalCount(res.total_count);
            setHasMore(res.has_more);
        } catch (e) {
            setPageError(e instanceof Error ? e.message : "Не удалось загрузить статистику");
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadPage(0, false);
    }, [period, segment]);

    const handleLoadMore = () => {
        loadPage(rows.length, true);
    };

    const togglePeriod = async (row: GeneralStatsPeriodRow) => {
        const isOpen = expandedPeriods.has(row.key);
        const next = new Set(expandedPeriods);
        if (isOpen) {
            next.delete(row.key);
            setExpandedPeriods(next);
            return;
        }
        next.add(row.key);
        setExpandedPeriods(next);

        if (!eventsByPeriod[row.key]) {
            setEventsByPeriod((prev) => ({ ...prev, [row.key]: { status: "loading" } }));
            try {
                const res = await getGeneralStatisticsEventsRequest(period, row.key, segment);
                setEventsByPeriod((prev) => ({
                    ...prev,
                    [row.key]: { status: "loaded", events: res.events },
                }));
            } catch (e) {
                setEventsByPeriod((prev) => ({
                    ...prev,
                    [row.key]: {
                        status: "error",
                        error: e instanceof Error ? e.message : "Не удалось загрузить мероприятия",
                    },
                }));
            }
        }
    };

    const toggleEvent = (periodKey: string, tourId: string) => {
        const eventKey = `${periodKey}:${tourId}`;
        const next = new Set(expandedEvents);
        if (next.has(eventKey)) {
            next.delete(eventKey);
        } else {
            next.add(eventKey);
        }
        setExpandedEvents(next);
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Общая статистика"
                description="Заказы, оплаченные или ещё активные (без учёта удалённых и бесплатных), по периодам."
            />

            <Card>
                <section className="section-toolbar">
                    <div>
                        <h3 className="card-title">Фильтры</h3>
                        <p className="card-text">
                            Период группировки и сегмент заказов — таблица обновится автоматически.
                        </p>
                    </div>
                </section>

                <div className="stats-toggle-group" role="group" aria-label="Период группировки">
                    {PERIOD_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`stats-toggle-btn ${!buyerTypeView && period === opt.value ? "stats-toggle-btn-active" : ""}`}
                            onClick={() => {
                                setBuyerTypeView(false);
                                setPeriod(opt.value);
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={`stats-toggle-btn ${buyerTypeView ? "stats-toggle-btn-active" : ""}`}
                        onClick={() => setBuyerTypeView(true)}
                    >
                        По типу покупателя
                    </button>
                </div>

                {buyerTypeView ? (
                    <BuyerTypeStatistics />
                ) : (
                <>
                <div className="stats-toggle-group" role="group" aria-label="Сегмент заказов">
                    {SEGMENT_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`stats-toggle-btn ${segment === opt.value ? "stats-toggle-btn-active" : ""}`}
                            onClick={() => setSegment(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                {pageError && <div className="form-global-error">{pageError}</div>}

                <div>
                {isLoading ? (
                    <div className="table-skeleton">
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="empty-block">
                        <h3 className="empty-block-title">Данных нет</h3>
                        <p className="empty-block-text">Попробуйте выбрать другой период или сегмент.</p>
                    </div>
                ) : (
                    <div className="table-wrap stats-general-table-wrap">
                        <table className="data-table stats-table stats-general-table">
                            <thead>
                            <tr>
                                <th>{columnLabel}</th>
                                <th>Заказов</th>
                                <th>Мест</th>
                                <th>На сумму</th>
                            </tr>
                            </thead>
                            <tbody>
                            {rows.map((row) => {
                                const isOpen = expandedPeriods.has(row.key);
                                const eventsState = eventsByPeriod[row.key];
                                return (
                                    <React.Fragment key={row.key}>
                                        <tr className="stats-period-row">
                                            <td className="stats-row-name">
                                                <button
                                                    type="button"
                                                    className={`stats-period-toggle ${isOpen ? "stats-period-toggle-open" : ""}`}
                                                    onClick={() => togglePeriod(row)}
                                                >
                                                    {row.label}
                                                </button>
                                            </td>
                                            <td className="stats-cell-left">{row.orders_count}</td>
                                            <td className="stats-cell-left">{row.seats_count}</td>
                                            <td className="stats-cell-left">{formatMoney(row.sum_amount)}</td>
                                        </tr>
                                        {isOpen && (!eventsState || eventsState.status === "loading") && (
                                            <tr className="stats-event-row">
                                                <td colSpan={4}>
                                                    <div className="table-skeleton">
                                                        <div className="table-skeleton-row" />
                                                        <div className="table-skeleton-row" />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {isOpen && eventsState?.status === "error" && (
                                            <tr className="stats-event-row">
                                                <td colSpan={4}>
                                                    <div className="form-global-error">{eventsState.error}</div>
                                                </td>
                                            </tr>
                                        )}
                                        {isOpen && eventsState?.status === "loaded" && eventsState.events && eventsState.events.length === 0 && (
                                            <tr className="stats-event-row">
                                                <td colSpan={4} className="stats-cell-muted">
                                                    Нет мероприятий за этот период.
                                                </td>
                                            </tr>
                                        )}
                                        {isOpen &&
                                            eventsState?.status === "loaded" &&
                                            eventsState.events &&
                                            eventsState.events.map((ev) => {
                                                const eventKey = `${row.key}:${ev.id}`;
                                                const eventOpen = expandedEvents.has(eventKey);
                                                return (
                                                    <React.Fragment key={ev.id}>
                                                        <tr className="stats-event-row">
                                                            <td className="stats-row-name">
                                                                <button
                                                                    type="button"
                                                                    className={`stats-period-toggle ${eventOpen ? "stats-period-toggle-open" : ""}`}
                                                                    onClick={() => toggleEvent(row.key, ev.id)}
                                                                >
                                                                    {ev.name}
                                                                </button>
                                                            </td>
                                                            <td className="stats-cell-left">{ev.orders_count}</td>
                                                            <td className="stats-cell-left">{ev.tickets_sold}</td>
                                                            <td className="stats-cell-left">{formatMoney(ev.sum_amount)}</td>
                                                        </tr>
                                                        {eventOpen &&
                                                            ev.cities.map((city) => (
                                                                <tr className="stats-event-city-row" key={city.id}>
                                                                    <td className="stats-row-name">{city.name}</td>
                                                                    <td className="stats-cell-left">{city.orders_count}</td>
                                                                    <td className="stats-cell-left">{city.tickets_sold}</td>
                                                                    <td className="stats-cell-left">{formatMoney(city.sum_amount)}</td>
                                                                </tr>
                                                            ))}
                                                    </React.Fragment>
                                                );
                                            })}
                                    </React.Fragment>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                )}
                </div>

                {!isLoading && hasMore && (
                    <div className="stats-load-more">
                        <button
                            type="button"
                            className="ui-button ui-button-secondary"
                            onClick={handleLoadMore}
                            disabled={isLoadingMore}
                        >
                            {isLoadingMore ? "Загрузка..." : `Показать ещё (${rows.length} из ${totalCount})`}
                        </button>
                    </div>
                )}
                </>
                )}
            </Card>
        </div>
    );
};

export default GeneralStatisticsPage;
